<template>
  <data-manager-layout>
    <el-button
      v-if="!readonly"
      slot="actions"
      type="primary"
      icon="el-icon-circle-plus"
      @click="addRow"
    >
      {{ $t('DataManager_Button_CreateTag') }}
    </el-button>

    <rest-list-editable
      :payload="fetchEndpoint"
      :after-fetch="transformTags"
      ref="rest-list-editable"
      :rules="rules"
      :add-endpoint="rowAddEndpoint"
      :update-endpoint="rowUpdateEndpoint"
    >
      <rest-list-column
        prop="title"
        :title="$t('DataManager_Tags_Field_Title')"
        :editable="true"
      >
        <template slot="input-edit" slot-scope="{ model }">
          <el-input v-model="model.title" :disabled="!!model.id" />
        </template>

        <template slot="default" slot-scope="scope">
          <tag-status :model="scope.row" />
          {{ scope.row.title }}
        </template>
      </rest-list-column>

      <rest-list-column
        prop="name"
        :title="$t('DataManager_Tags_Field_Name')"
        :editable="true"
      />

      <rest-list-column
        prop="alias"
        :title="$t('DataManager_Tags_Field_Alias')"
        :editable="true"
      >
        <template slot="input-edit" slot-scope="{ model }">
          <el-autocomplete
            v-model="model.alias"
            :fetch-suggestions="aliasAutocomplete"
          />
        </template>
      </rest-list-column>

      <rest-list-column-enum
        prop="type"
        :title="$t('DataManager_Tags_Field_Type')"
        :editable="true"
        :enum="TagTypeLabels"
        :order="TagTypeOrder"
        :number="true"
      >
        <template slot="default" slot-scope="scope">
          {{ $t(TagTypeInfo[scope.row.type].label) }}

          <el-tooltip v-if="TagTypeInfo[scope.row.type].unenforced">
            <div slot="content">
              {{ $t('DataManager_Tags_Tooltip_TypeNotEnforced') }}
            </div>
            <el-tag
              type="warning"
              size="small"
              style="cursor: pointer"
              @click="() => convertType(scope.row)"
            >
              Unenforced
            </el-tag>
          </el-tooltip>
        </template>

        <template slot="input-edit" slot-scope="{ model }">
          <slot name="input-edit" :model="model">
            <tag-type-select
              v-model="model.type"
              :allow-unenforced="2"
              :show-example="true"
              :disabled="!!model.id"
            />
          </slot>
        </template>
      </rest-list-column-enum>

      <rest-list-column-enum
        prop="category_id"
        :title="$t('DataManager_Tags_Field_Category')"
        :editable="true"
        :enum="categories.items"
        :order="categories.order"
        :translate="false"
        :number="true"
        v-if="areCategoriesEnabled"
      >
      </rest-list-column-enum>

      <rest-list-column-boolean
        prop="attribute_client"
        :title="$t('DataManager_Tags_Field_AttributeClient')"
        :editable="true"
      />

      <rest-list-column-boolean
        prop="attribute_profile"
        :title="$t('DataManager_Tags_Field_AttributeProfile')"
        :editable="true"
      />

      <rest-list-column-boolean
        v-if="$isSuperAdmin()"
        prop="process_tag_hit"
        :title="$t('DataManager_Tags_Field_ProcessTagHit')"
        :editable="true"
      />

      <rest-list-column-boolean
        prop="sdk_update"
        :title="$t('DataManager_Tags_Field_SDKUpdate')"
        :editable="true"
      />

      <el-table-column
        :width="50"
        align="center"
        class-name="editable-row-actions"
        :fixed="false"
      >
        <template slot-scope="scope" v-if="!readonly">
          <i
            v-if="scope.row.is_deleted"
            @click="() => cancelDelete(scope.row)"
            class="el-icon-warning-outline"
            :title="$t('DataManager_Tags_Action_CancelDelete')"
          />
          <rest-list-action-edit
            v-else-if="!scope.row.status"
            :index="scope.$index"
            icon="el-icon-more"
          >
            <rest-list-action
              v-if="!isOpenSearchEnabled"
              icon="el-icon-position"
              :click="() => convertType(scope.row)"
              title="Convert"
            />
            <rest-list-action-delete
              :endpoint="rowDeleteEndpoint(scope.row)"
              :name="scope.row.title"
            />
          </rest-list-action-edit>
        </template>
      </el-table-column>
    </rest-list-editable>

    <attribute-conversion-dialog ref="attribute-conversion-dialog" />
  </data-manager-layout>
</template>

<script>
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnBoolean from '@/common/components/rest/RestListColumnBoolean'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import {
  TagTypeLabels,
  TagTypeInfo,
  TagTypeOrder
} from '@/modules/data-manager/enum/TagTypeEnum'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import DataManagerLayout from '@/modules/data-manager/pages/DataManagerLayout'
import RestListEditable from '@/common/components/rest/RestListEditable'
import RestListActionEdit from '@/common/components/rest/RestListActionEdit'
import AttributeConversionDialog from '../components/AttributeConversionDialog'
import TagTypeSelect from '../components/TagTypeSelect'
import RestListAction from '../../../common/components/rest/RestListAction'
import RestListColumnEnum from '../../../common/components/rest/RestListColumnEnum'
import TagStatus from '../components/TagStatus'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  name: 'DataManagerAttributesTagsPage',

  components: {
    TagStatus,
    RestListColumnEnum,
    RestListAction,
    TagTypeSelect,
    AttributeConversionDialog,
    RestListActionEdit,
    RestListEditable,
    DataManagerLayout,
    RestListActionDelete,
    RestListColumn,
    RestListColumnBoolean
  },

  data() {
    return {
      categories: { items: {}, order: [] },
      TagTypeLabels,
      TagTypeInfo,
      TagTypeOrder
    }
  },

  mounted() {
    if (this.areCategoriesEnabled) {
      this.initializeCategories()
    }
  },

  computed: {
    rules() {
      const titleRules = [
        {
          required: true,
          trigger: 'blur',
          message: this.$t('DataManager_Tags_Validation_Required')
        }
      ]

      if (this.$hasFeature('stricterTagValidation')) {
        titleRules.push({
          validator: (rule, value, callback) => {
            const ref = this.$refs['rest-list-editable']
            const editableRow = ref && ref.editableRow
            if (editableRow && editableRow.id) {
              callback()
              return
            }

            if (!value) {
              callback()
              return
            }

            if (!/^[a-zA-Z][a-zA-Z0-9_ ]*$/.test(value.trim())) {
              if (/^[0-9]+$/.test(value)) {
                callback(
                  new Error(this.$t('DataManager_Tags_Validation_AllNumbers'))
                )
                return
              }

              if (/^[0-9]/.test(value)) {
                callback(
                  new Error(
                    this.$t('DataManager_Tags_Validation_StartsWithNumber')
                  )
                )
                return
              }

              callback(
                new Error(this.$t('DataManager_Tags_Validation_InvalidFormat'))
              )
              return
            }

            callback()
          },
          trigger: 'blur'
        })
      }

      return {
        title: titleRules,
        type: {
          required: true,
          trigger: 'blur'
        }
      }
    },
    fetchEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `tags`,
        params: {
          sort: 'title',
          aggregate: 0
        }
      })
    },

    rowAddEndpoint() {
      return projectEndpoint({
        method: 'post',
        url: `tags`,
        handler: this.transformTag
      })
    },

    readonly() {
      return !this.$can('manageProjectTechnical')
    },

    restList() {
      return this.$refs['rest-list-editable'].$refs['restList']
    },

    areCategoriesEnabled() {
      return $hasFeature('tagCategories')
    },

    isOpenSearchEnabled() {
      return $hasFeature('openSearchEnabled')
    }
  },

  methods: {
    addRow() {
      const model = {
        process_tag_hit: 1,
        sdk_update: 0
      }
      this.$refs['rest-list-editable'].addRow(model)
    },

    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `tags/${row.id}`
      })
    },

    rowUpdateEndpoint(row) {
      return projectEndpoint({
        method: 'patch',
        url: `tags/${row.id}`,
        handler: this.transformTag
      })
    },

    transformTags() {
      // eslint-disable-next-line standard/computed-property-even-spacing
      this.$refs['rest-list-editable'].$refs['restList'].data = this.$refs[
        'rest-list-editable'
      ].$refs['restList'].data.map(item => {
        return this.transformTag(item)
      })
    },

    transformTag(item) {
      return {
        ...item,
        title: item.name,
        name: item.display_name
      }
    },

    aliasAutocomplete(queryString, cb) {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `tags`,
          params: {
            title: queryString,
            alias_id: 0
          }
        })
      ).then(({ data }) => {
        let list = data.reduce((arr, tag) => {
          arr.push({ value: tag.name })
          return arr
        }, [])

        cb(list)
      })
    },

    createAttributeIndex(row) {
      this.restList.loading = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `tags/${row.id}/create-attribute-index`
        })
      )
        .then(({ data }) => {
          this.$router.push(
            projectRoute({
              name: 'TaskHistoryView',
              query: { id: data.task_id }
            })
          )
        })
        .catch(error => {
          this.restList.loading = false
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    },

    removeAttributeIndex(row) {
      this.restList.loading = true
      RestApi(
        projectEndpoint({
          method: 'post',
          url: `tags/${row.id}/remove-attribute-index`
        })
      )
        .then(() => {
          this.$message({
            message: this.$t('RestList_DeleteSuccessMessage'),
            type: 'success'
          })
          this.restList.refresh()
        })
        .catch(error => {
          this.restList.loading = false
          this.$message({
            message: error.response.data.message,
            type: 'error'
          })
        })
    },

    cancelDelete(row) {
      this.$msgbox({
        title: this.$t('DataManager_Tags_Action_CancelDelete_Title'),
        message: this.$t('DataManager_Tags_Action_CancelDelete_Message'),
        showCancelButton: true,
        confirmButtonText: this.$t('DataManager_Tags_Action_CancelDelete_Keep'),
        cancelButtonText: this.$t('RestList_SaveCancelButton'),
        center: true
      }).then(() => {
        RestApi(
          projectEndpoint({
            method: 'patch',
            url: `tags/${row.id}`,
            data: {
              is_deleted: 0
            }
          })
        )
          .then(() => {
            this.$message({
              message: this.$t('RestList_CancelSuccessMessage'),
              type: 'success'
            })
            this.restList.refresh()
          })
          .catch(error => {
            this.restList.loading = false
            this.$message({
              message: error.message,
              type: 'error'
            })
          })
      })
    },

    convertType(row) {
      this.$refs['attribute-conversion-dialog'].open(row)
    },

    initializeCategories() {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `tag-categories`,
          params: { limit: 500 }
        })
      )
        .then(({ data }) => {
          let categories = { 0: '-' }
          data.forEach(category => {
            categories[category.id] = category.name
          })

          const sortedKeys = Object.entries(categories)
            .sort(([, a], [, b]) => a.localeCompare(b))
            .map(([key]) => key)

          this.categories.items = categories
          this.categories.order = sortedKeys
        })
        .catch(error => {
          this.$message({
            message: error.message,
            type: 'error'
          })
        })
    }
  }
}
</script>

<style scoped>
>>> .el-icon-warning-outline:hover {
  cursor: pointer;
}

>>> .el-select {
  width: 100%;
}

>>> .cell > .el-switch {
  display: flex;
  justify-content: center;
}

>>> .el-autocomplete {
  width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/modules/data-manager/pages/DataManagerAttributesTagsPage.vue